import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { IconButton, InputAdornment } from "@mui/material";
import ButtonBase from "components/shared/ButtonBase";
import InfoBlue from "components/shared/InfoBlockBlue";
import StyledCheckbox from "components/shared/StyledCheckbox";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useTracker } from "contexts/tracker";
import { SmallBody } from "helpers/TypographyComponents";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface AuthFirstLoginProps {
    username: string;
    newPassword: string;
    showPassword: boolean;
    error: boolean;
    errorMessage: string;
    isReadyToConnect: boolean;
    isLoading: boolean;
    isReadyToVerifyEmail: boolean;
    handleNewPassword: (e: any) => void;
    handleClickShowPassword: () => void;
    handleFirstLogin: () => void;
    handleVerificationCode: (e: any) => void;
    handleEmailVerified: () => void;
    handleResendCode: () => void;
}

const AuthFirstLogin = ({
    username,
    newPassword,
    showPassword,
    error,
    errorMessage,
    isReadyToConnect,
    isLoading,
    isReadyToVerifyEmail,
    handleNewPassword,
    handleClickShowPassword,
    handleFirstLogin,
    handleVerificationCode,
    handleEmailVerified,
    handleResendCode,
}: AuthFirstLoginProps) => {
    const tracker = useTracker();
    const [t] = useTranslation();
    const [hasAcceptedTos, setHasAcceptedTos] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(30);
    const [retryCount, setRetryCount] = useState(0);

    const passwordRequirements = useMemo(
        () => ({
            MinCharacter: newPassword.length >= 8,
            HasUpperCase: /[A-Z]/.test(newPassword),
            HasLowerCase: /[a-z]/.test(newPassword),
            HasNumber: /\d/.test(newPassword),
            HasSpecialChar: /\W/.test(newPassword),
        }),
        [newPassword],
    );

    const passwordHasAllRequirements = useMemo(() => {
        return Object.values(passwordRequirements).every((value) => value);
    }, [passwordRequirements]);

    const handleAskForCode = () => {
        handleResendCode();
        setRetryCount((prev) => ++prev);
        setSecondsLeft(30);
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setSecondsLeft((prevSeconds) => {
                if (prevSeconds === 0) {
                    clearInterval(interval);
                    return 0;
                }
                return --prevSeconds;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [retryCount]);

    useEffect(() => {
        if (tracker) {
            tracker.trackEvent("Page visit", {
                name: "Sign up",
            });
        }
    }, [tracker]);

    return (
        <>
            {(username == "" && <h1 className="trans-slide-in">{t("LogIn.ChangePassword")}</h1>) || (
                <h1 className="trans-slide-in">
                    {t("LogIn.Hello")} {username}
                </h1>
            )}
            <div className="login-left-container-fields trans-slide-in" key={"newPassword"}>
                <div className="login-left-container-field">
                    {!isReadyToVerifyEmail && (
                        <>
                            <div className="login-left-container-subtext">{t("LogIn.NewPassword")}</div>
                            <StyledTextField
                                placeholder="•••••••••••••"
                                type={showPassword ? "text" : "password"}
                                onChange={handleNewPassword}
                                disabled={isReadyToVerifyEmail}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClickShowPassword} edge="end">
                                                {showPassword ? (
                                                    <VisibilityOff fontSize="small" />
                                                ) : (
                                                    <Visibility fontSize="small" />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {Object.entries(passwordRequirements).map(([key, value]) => (
                                <SmallBody
                                    key={key}
                                    color={value ? "success" : "grey"}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        fontWeight: value ? "500" : "400",
                                    }}
                                >
                                    <CheckCircleOutlineIcon
                                        fontSize="small"
                                        htmlColor={value ? "var(--success)" : "var(--border-color)"}
                                    />
                                    {t(`LogIn.Password${key}`)}
                                </SmallBody>
                            ))}
                            <div className="login-left-tos">
                                <StyledCheckbox
                                    id="tos"
                                    onChange={(e) => setHasAcceptedTos(e.target.checked)}
                                ></StyledCheckbox>
                                <label
                                    htmlFor="tos"
                                    className="login-left-container-subtext"
                                    style={{ color: "black" }}
                                >
                                    {t("LogIn.Accept")}{" "}
                                    <a
                                        href={`${window.location.origin}/cgu_crealo.pdf`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {t("LogIn.TOS")}
                                    </a>
                                </label>
                            </div>
                        </>
                    )}
                    {isReadyToVerifyEmail && (
                        <>
                            <div className="login-left-container-subtext">{t("LogIn.VerifyEmail")}</div>
                            <StyledTextField onChange={handleVerificationCode} placeholder="000000" />
                            {!error && <InfoBlue info={t("LogIn.NewMailSent")} />}
                        </>
                    )}
                </div>
                {(!isReadyToVerifyEmail && (
                    <ButtonBase
                        variant="contained"
                        styles={{ borderRadius: "4px" }}
                        disabled={!isReadyToConnect || !passwordHasAllRequirements}
                        loading={isLoading}
                        onClick={handleFirstLogin}
                    >
                        {t("LogIn.CreateNewPassword")}
                    </ButtonBase>
                )) || (
                    <ButtonBase
                        variant="contained"
                        styles={{ borderRadius: "4px" }}
                        disabled={!isReadyToConnect || !hasAcceptedTos}
                        loading={isLoading}
                        onClick={handleEmailVerified}
                    >
                        {t("LogIn.Verify")}
                    </ButtonBase>
                )}
                {isReadyToVerifyEmail && (
                    <button className="resend-code-btn" onClick={() => handleAskForCode()} disabled={secondsLeft > 0}>
                        {t("LogIn.NoCodeReceived")}{" "}
                        {secondsLeft > 0 ? t("LogIn.BePatient", { seconds: secondsLeft }) : t("LogIn.SendNewCode")}
                    </button>
                )}
                {error && <div className="login-left-container-error">{errorMessage || t("LogIn.WentWrong")}</div>}
            </div>
        </>
    );
};

export default AuthFirstLogin;
